import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CITIES, CITY_SLUGS, type City } from "@/data/cities";
import { CITY_MARIAGE_UNIQUE } from "@/data/city-mariage-unique";
import { VENUE_LIST } from "@/data/venues";


import { useReveal } from "@/hooks/use-reveal";
import { RentalPromo } from "@/components/rental-promo";
import { SeoAccordion } from "@/components/seo-accordion";
import { DJ_VAL_SECTIONS } from "@/data/seo-blocks";

import danceImg from "@/assets/dj-mariage-dance.jpg";
import sparksImg from "@/assets/dj-mariage-sparks.jpg";
import equipmentImg from "@/assets/dj-mariage-equipment.jpg";
import ceremonyImg from "@/assets/dj-mariage-ceremony.jpg";
import receptionImg from "@/assets/dj-mariage-reception.jpg";
import servicesImg from "@/assets/dj-mariage-services.jpg";
import { trackEvent } from "@/lib/analytics";
import { HeroVideo } from "@/components/hero-video";
import aixVideo from "@/assets/dj-mariage-aix-hero.mp4.asset.json";
import aixVideoMobile from "@/assets/dj-mariage-aix-hero-640.mp4.asset.json";
import aixPoster from "@/assets/dj-mariage-aix-hero-poster.jpg.asset.json";
import aixHlReception from "@/assets/aix-highlight-reception.jpg";
import aixHlCocktail from "@/assets/aix-highlight-cocktail.jpg";
import aixHlBatterie from "@/assets/aix-highlight-batterie.jpg";
import aixHlLimiteur from "@/assets/aix-highlight-limiteur.jpg";
import aixHlDiscretion from "@/assets/aix-highlight-discretion.jpg";
import aixHlExclusivite from "@/assets/aix-highlight-exclusivite.jpg";

const AIX_HIGHLIGHT_IMAGES: Record<string, string> = {
  "Réceptions chic": aixHlReception,
  "Cocktail & dîner soignés": aixHlCocktail,
  "Système autonome sur batterie": aixHlBatterie,
  "Limiteur maîtrisé": aixHlLimiteur,
  "Discrétion absolue": aixHlDiscretion,
  "1 seul mariage par week-end": aixHlExclusivite,
};
import { metaDesc } from "@/lib/seo-description";
import { lazy, Suspense } from "react";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";

const GoogleReviewsCarousel = lazy(() =>
  import("@/components/google-reviews-carousel").then((m) => ({ default: m.GoogleReviewsCarousel })),
);



export const Route = createFileRoute("/dj-mariage/$ville")({
  loader: ({ params }) => {
    const base = CITIES[params.ville];
    if (!base) throw notFound();
    // Contenu éditorial unique par ville (anti-doorway) : fusionné avec la fiche.
    const unique = CITY_MARIAGE_UNIQUE[params.ville];
    const city: City = unique
      ? {
          ...base,
          sections: base.sections ?? unique.sections,
          caseStudy: base.caseStudy ?? unique.caseStudy,
          faq: [...base.faq, ...unique.faq.filter((u) => !base.faq.some((f) => f.q === u.q))],
        }
      : base;
    return { city };
  },

  head: ({ loaderData }) => {
    const c = loaderData?.city as City | undefined;
    if (!c) return { meta: [{ title: "DJ Mariage" }] };
    return {
      meta: [
        { title: c.metaTitle },
        { name: "description", content: metaDesc(c.metaDescription) },
        { property: "og:title", content: c.metaTitle },
        { property: "og:description", content: metaDesc(c.metaDescription) },
        { property: "og:image", content: c.hero },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `https://dj-val.fr/dj-mariage/${c.slug}` },
      ],
      links: [
        { rel: "canonical", href: `https://dj-val.fr/dj-mariage/${c.slug}` },
        { rel: "preload", as: "image", href: c.hero, fetchpriority: "high" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({ ...LOCAL_BUSINESS_SCHEMA, "@context": "https://schema.org", name: `DJ Val – ${c.title}`, areaServed: { "@type": "Place", name: c.name } }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
              { "@type": "ListItem", position: 2, name: "DJ Mariage", item: "https://dj-val.fr/dj-mariage" },
              { "@type": "ListItem", position: 3, name: c.name, item: `https://dj-val.fr/dj-mariage/${c.slug}` },
            ],
          }),
        },

      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground">
      <div className="text-center">
        <h1 className="font-serif-display text-4xl">Ville non couverte</h1>
        <Link to="/dj-mariage" className="mt-4 inline-block text-[var(--gold)] underline">
          Retour à DJ Mariage
        </Link>
      </div>
    </div>
  ),
  component: CityPage,
});

function CityPage() {
  useReveal();
  const { city } = Route.useLoaderData();
  const c: City = city;
  const prep = c.preposition || "à";

  return (
    <div className="bg-background text-muted-foreground">
      {/* HERO */}
      <section className="on-dark relative min-h-[80vh] flex items-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img src={c.hero} alt={`DJ mariage ${prep} ${c.name}`} className="h-full w-full object-cover opacity-40 kenburns-img" fetchPriority="high" decoding="async" loading="eager" width={1920} height={1080} />
          {c.slug === "aix-en-provence" && (
            <HeroVideo src={aixVideo.url} srcMobile={aixVideoMobile.url} poster={aixPoster.url} className="absolute inset-0 h-full w-full object-cover" />
          )}
        </div>
        <div className="relative z-10 max-w-3xl reveal space-y-6">
          <p className="eyebrow">DJ Mariage · {c.region}</p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="italic font-light">{c.h1.split(" ").slice(0, 2).join(" ")}</span>
            <span className="block font-light gold-text-shine mt-2">{c.h1.split(" ").slice(2).join(" ") || c.name}</span>
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-foreground/85 font-light leading-relaxed max-w-xl">{c.intro}</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contactez-nous" className="bg-[var(--gold)] text-[var(--ink)] px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
              Devis gratuit
            </Link>
            <a href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "dj_mariage_ville" })} className="border border-[var(--gold-line)] text-foreground px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>

      {/* CROSS-LINK — page hub départementale (anti-cannibalisation) */}
      {c.slug === "vaucluse" && (
        <section className="border-y border-[var(--gold-line)] bg-card px-6 py-5">
          <div className="max-w-4xl mx-auto text-center text-sm font-light space-y-2">
            <p className="text-muted-foreground">
              Cette page est consacrée au mariage. Pour un anniversaire, une soirée privée ou un événement
              d'entreprise, consultez le hub{" "}
              <Link to="/dj-vaucluse" className="text-[var(--gold)] underline underline-offset-4">
                DJ dans le Vaucluse, toutes prestations
              </Link>
              , ou la page{" "}
              <Link to="/dj-avignon" className="text-[var(--gold)] underline underline-offset-4">
                DJ à Avignon
              </Link>
              .
            </p>
          </div>
        </section>
      )}

      {/* LIEUX DE MARIAGE — fiches détaillées */}
      {c.slug === "vaucluse" && (
        <section className="py-20 px-6 max-w-6xl mx-auto">
          <div className="text-center mb-10 sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Lieux de mariage du Vaucluse</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
              Des domaines que je connais <span className="italic">de l'intérieur</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {VENUE_LIST.filter((v) => v.region.includes("Vaucluse") || v.region.includes("Luberon")).map((v) => (
              <Link
                key={v.slug}
                to="/lieux/$slug"
                params={{ slug: v.slug }}
                className="px-5 py-2.5 border border-[var(--gold-line)] bg-card text-foreground/90 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
              >
                Mariage au {v.name} · {v.city}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* HIGHLIGHTS */}

      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Pourquoi DJ Val</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Le DJ <span className="italic">référent</span> {prep} {c.name}
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-3 border border-[var(--gold-line)]">
          {c.highlights.map((h, i) => (
            <div key={h.title} className="bg-background p-10 sr" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
              {AIX_HIGHLIGHT_IMAGES[h.title] && (
                <img
                  src={AIX_HIGHLIGHT_IMAGES[h.title]}
                  alt={h.title}
                  loading="lazy"
                  width={1024}
                  height={768}
                  className="w-full aspect-[4/3] object-cover mb-6"
                />
              )}
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-serif-display text-2xl text-foreground mt-4">{h.title}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{h.text}</p>
            </div>
          ))}
        </div>
      </section>

      <Suspense fallback={<div className="min-h-[30vh]" />}>
        <GoogleReviewsCarousel />
      </Suspense>

      {/* LOCAL INSIGHT */}
      {c.localInsight && (
        <section className="py-16 px-6 border-y border-[var(--gold-line)] bg-card">
          <div className="max-w-3xl mx-auto text-center sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Notre zone</p>
            <p className="font-serif-display italic text-2xl text-foreground/90 mt-6 leading-relaxed">
              « {c.localInsight} »
            </p>
          </div>
        </section>
      )}

      {/* PARTENAIRES — domaines, châteaux, salles avec liens externes */}
      {c.partners && c.partners.length > 0 && (
        <section className="py-24 px-6 max-w-7xl mx-auto">
          <div className="text-center mb-16 sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Lieux d'exception {prep} {c.name}</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
              Les <span className="italic">plus beaux domaines</span> autour de chez vous
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
            <p className="text-sm text-muted-foreground font-light mt-6 max-w-2xl mx-auto">
              Une sélection de domaines, châteaux et salles de réception où j'interviens régulièrement.
              Cliquez pour découvrir leur site et leurs prestations.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {c.partners.map((p, i) => {
              const inner = (
                <>
                  <p className="eyebrow text-[var(--gold)] text-[10px]">{p.type}</p>
                  <h3 className="font-serif-display text-2xl text-foreground mt-3">{p.name}</h3>
                  <span className="gold-rule !w-8 my-4" />
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">{p.desc}</p>
                  {p.url && (
                    <span className="mt-5 inline-flex items-center text-[11px] uppercase tracking-[0.2em] text-foreground group-hover:text-[var(--gold)] transition-colors">
                      Visiter le site →
                    </span>
                  )}
                </>
              );
              return p.url ? (
                <a
                  key={p.name}
                  href={p.url}
                  target="_blank"
                  rel="noopener"
                  className="group sr block border border-[var(--gold-line)] bg-card p-8 hover:border-[var(--gold)] transition-colors"
                  data-reveal
                  style={{ transitionDelay: `${(i % 3) * 80}ms` }}
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={p.name}
                  className="sr block border border-[var(--gold-line)] bg-card p-8"
                  data-reveal
                  style={{ transitionDelay: `${(i % 3) * 80}ms` }}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* CATERERS — traiteurs partenaires */}
      {c.caterers && c.caterers.length > 0 && (
        <section className="py-20 px-6 border-y border-[var(--gold-line)] bg-card">
          <div className="max-w-5xl mx-auto sr" data-reveal>
            <div className="text-center mb-12">
              <p className="eyebrow text-[var(--gold)]">Traiteurs avec qui je travaille</p>
              <h2 className="font-serif-display text-4xl text-foreground mt-4">
                Une <span className="italic">cuisine</span> à la hauteur de la fête
              </h2>
              <span className="gold-rule mx-auto !w-12 mt-6" />
              <p className="text-sm text-muted-foreground font-light mt-6 max-w-2xl mx-auto">
                Des traiteurs locaux reconnus, croisés sur de nombreux mariages {prep} {c.name}. Le timing entre service et dancefloor n'est jamais laissé au hasard.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {c.caterers.map((t) => {
                const inner = (
                  <>
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 className="font-serif-display text-xl text-foreground">{t.name}</h3>
                      <span className="eyebrow text-[var(--gold)] text-[10px] shrink-0">{t.type}</span>
                    </div>
                    <span className="gold-rule !w-8 my-3" />
                    <p className="text-sm text-muted-foreground leading-relaxed font-light">{t.desc}</p>
                  </>
                );
                return t.url ? (
                  <a key={t.name} href={t.url} target="_blank" rel="noopener" className="block border border-[var(--gold-line)] bg-background p-6 hover:border-[var(--gold)] transition-colors">
                    {inner}
                  </a>
                ) : (
                  <div key={t.name} className="block border border-[var(--gold-line)] bg-background p-6">
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* VENUES — tags simples (fallback ou complément) */}
      {c.venues.length > 0 && (
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto text-center sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Autres lieux où j'interviens {prep} {c.name}</p>
            <span className="gold-rule mx-auto !w-12 my-8" />
            <div className="flex flex-wrap justify-center gap-3">
              {c.venues.map((v) => (
                <span key={v} className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/90 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
                  {v}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AMBIANCE — galerie d'inspiration */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">L'ambiance</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Une <span className="italic">soirée signée</span> DJ Val {prep} {c.name}
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
          <p className="text-sm text-muted-foreground font-light mt-6 max-w-2xl mx-auto">
            Du vin d'honneur à la dernière danse : une mise en lumière soignée, un son cristallin et une piste qui ne désemplit pas.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { src: receptionImg, alt: `Mise en lumière LED d'une réception de mariage ${prep} ${c.name} par DJ Val` },
            { src: danceImg, alt: `Piste de danse pleine animée par DJ Val lors d'un mariage ${prep} ${c.name}` },
            { src: sparksImg, alt: `Étincelles froides ouverture de bal d'un mariage ${prep} ${c.name}` },
            { src: ceremonyImg, alt: `Sonorisation de cérémonie laïque en extérieur ${prep} ${c.name} — micros HF Shure` },
            { src: equipmentImg, alt: `Régie Pioneer CDJ-3000 · DJM-A9 et sono RCF installées pour un mariage ${prep} ${c.name}` },
            { src: servicesImg, alt: `Installation complète sono + éclairage pour soirée privée ${prep} ${c.name}` },
          ].map((img, i) => (
            <div
              key={i}
              className="group overflow-hidden border border-[var(--gold-line)] sr aspect-[4/3]"
              data-reveal
              style={{ transitionDelay: `${(i % 3) * 80}ms` }}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                decoding="async"
                width={800}
                height={600}
                className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-110"
              />
            </div>
          ))}
        </div>
      </section>

      {/* SECTIONS RÉDIGÉES — contenu propre à la ville */}
      {c.sections?.map((s) => (
        <section key={s.h2} className="py-20 px-6 border-t border-[var(--gold-line)]">
          <div className="max-w-3xl mx-auto sr" data-reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">{s.h2}</h2>
            <span className="gold-rule !w-12 my-8" />
            <div className="space-y-5">
              {s.paragraphs.map((p, i) => (
                <p key={i} className="text-[15px] text-muted-foreground font-light leading-[1.9]">{p}</p>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* RETOUR D'EXPÉRIENCE — cas concret dans la ville */}
      {c.caseStudy && (
        <section className="py-24 px-6 border-t border-[var(--gold-line)] bg-card">
          <div className="max-w-3xl mx-auto sr" data-reveal>
            <p className="eyebrow text-[var(--gold)]">Retour d'expérience {prep} {c.name}</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">{c.caseStudy.title}</h2>
            <span className="gold-rule !w-12 my-8" />
            <p className="text-[15px] text-muted-foreground font-light leading-[1.9]">{c.caseStudy.text}</p>
          </div>
        </section>
      )}

      {/* LOCATION MATÉRIEL — promo prestation {prep} {c.name} */}
      <RentalPromo cityName={c.name} prep={prep} context="mariage" />




      {/* FAQ */}
      <section className="py-24 px-6 max-w-4xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Questions fréquentes</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">DJ mariage {prep} {c.name}</h2>

          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {c.faq.map((f) => (
            <details key={f.q} className="group border border-[var(--gold-line)] bg-card p-6 sr" data-reveal>
              <summary className="cursor-pointer font-serif-display text-xl text-foreground flex justify-between items-center gap-4">
                {f.q}
                <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
            </details>
          ))}
        </div>
        <p className="mt-8 text-sm text-muted-foreground font-light leading-relaxed text-center">
          Pour aller plus loin, consultez le guide détaillé des{" "}
          <Link
            to="/actualites/$slug"
            params={{ slug: "prix-dj-mariage-provence-guide-complet-2026" }}
            className="text-[var(--gold)] underline underline-offset-4"
          >
            tarifs d'un DJ de mariage en Provence
          </Link>{" "}
          : budgets réels, options et postes de dépense.
        </p>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center border-t border-[var(--gold-line)]">

        <div className="max-w-2xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-4xl text-foreground">
            Votre mariage {prep} {c.name} <span className="italic text-[var(--gold)]">mérite</span> un DJ pro
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
            Demander un devis
          </Link>
        </div>
      </section>

      {/* Cross-contexte — autres prestations dans la même ville */}
      <section className="py-16 px-6 border-t border-[var(--gold-line)]">
        <div className="max-w-4xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Autres prestations {prep} {c.name}</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <Link to="/dj-entreprise/$ville" params={{ ville: c.slug }} className="border border-[var(--gold-line)] bg-card p-6 text-left hover:border-[var(--gold)] transition-colors">
              <p className="font-serif-display text-xl text-foreground">DJ soirée d'entreprise {prep} {c.name}</p>
              <span className="gold-rule !w-8 my-3" />
              <p className="text-xs text-muted-foreground font-light">Gala, séminaire, kick-off, soirée CE — facturation pro, SACEM, RC Pro.</p>
            </Link>
            <Link to="/dj-anniversaire/$ville" params={{ ville: c.slug }} className="border border-[var(--gold-line)] bg-card p-6 text-left hover:border-[var(--gold)] transition-colors">
              <p className="font-serif-display text-xl text-foreground">DJ anniversaire {prep} {c.name}</p>
              <span className="gold-rule !w-8 my-3" />
              <p className="text-xs text-muted-foreground font-light">30, 40, 50, 60 ans, surprise party — toutes générations, forfait clé en main.</p>
            </Link>
          </div>
          <p className="mt-6 text-xs text-muted-foreground font-light">
            Option la plus demandée :{" "}
            <Link to="/location-photobooth-avignon" className="text-[var(--gold)] underline underline-offset-4">
              le photobooth dès 190 €
            </Link>{" "}
            — livraison, installation et démontage offerts avec une prestation DJ.
          </p>
        </div>
      </section>

      {/* Other cities */}
      <section className="py-20 px-6 border-t border-[var(--gold-line)] bg-card">
        <div className="max-w-6xl mx-auto">
          <p className="eyebrow text-[var(--gold)] text-center">Autres villes</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {CITY_SLUGS.filter((s) => s !== c.slug).map((s) => (
              <Link
                key={s}
                to="/dj-mariage/$ville"
                params={{ ville: s }}
                className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/85 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
              >
                {CITIES[s].name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <SeoAccordion
        eyebrow={`DJ Mariage · ${c.name}`}
        title={<>Tout savoir avant de réserver <span className="italic">à {c.name}</span></>}
        intro={`Tarifs, délais, lieux de réception, matériel et logistique pour un mariage ou un événement ${c.preposition ?? "à"} ${c.name}.`}
        sections={DJ_VAL_SECTIONS}
      />
    </div>
  );
}
